import { MEDIA_KEYS } from '@sonifi/wb-media/constants/WbMediaConstants';
import { parseHexToColor } from '@sonifi/wb-screenbuilder/components/HappeningsBuilder/services/color.service';
import {
  DATE_FORMATS,
  parseDate,
} from '@sonifi/wb-widget/components/WbFormatters/WbDate';
import {
  INVALID_DURATION,
  NULL_MEDIA,
  MAX_DURATION,
  MIN_DURATION,
} from '../constants/wbHappeningsConstants';
import { defaultConfig } from '../../../config-env';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import { buildApiUri } from '@sonifi/wb-core/utils/parseHelper';

export const fetchTimelineDurationLimit = ({
  channelSettings,
  timeline,
  timelineList,
}) => {
  const happeningsTypes = ['happenings', 'happenings+'];
  let durationLimit = 20 * 60;

  if (happeningsTypes.includes(timeline.type)) {
    const channelSettingsIndex = timelineList
      .filter((model) => happeningsTypes.includes(model.type))
      .sort(function (a, b) {
        if (a.number === b.number) {
          return 0;
        }

        return a.number < b.number ? -1 : 1;
      })
      .findIndex((model) => model.uri === timeline.uri);

    durationLimit = channelSettings[channelSettingsIndex].duration_limit;
  }

  const dateDL = new Date(0, 0, 0, 0, 0, durationLimit, 0);
  const timelineDurationLimit = dateDL.getHours() * 60 + dateDL.getMinutes();
  return timelineDurationLimit;
};

export const getClipStartEndDate = (item) => {
  let startDate = new Date();
  let endDate = null;

  if (item.media.active) {
    startDate = item.media.active.start_date
      ? new Date(item.media.active.start_date)
      : new Date();

    endDate = item.media.active.end_date
      ? new Date(item.media.active.end_date)
      : null;
  }

  return {
    start_date: startDate.toISOString(),
    end_date: endDate ? endDate.toISOString() : null,
  };
};

export const getUseDefaultDuration = ({ type, duration }) =>
  type !== MEDIA_KEYS.TYPE.VIDEOS.KEY &&
  (!Number.isFinite(duration) || duration === INVALID_DURATION);

export const hasThumbnail = (clip) =>
  typeof clip.media.file_uri !== 'undefined';

export const isInvalid = (clip) =>
  clip.media === NULL_MEDIA || clip.media.isZombie;

export const getIsValidDateRange = (clip) => {
  const hasEndDate = !!clip.media.active?.end_date;
  const hasStartDate = !!clip.media.active?.start_date;
  let startDateTime;
  let endDateTime;
  if (hasEndDate) {
    const endDate = new Date(clip.media.active.end_date);
    endDate.setHours(23);
    endDate.setMinutes(59);
    endDate.setSeconds(59);
    endDateTime = endDate.getTime();
  }

  if (hasStartDate) {
    const startDate = new Date(clip.media.active.start_date);
    startDateTime = startDate.getTime();
  }

  // Is valid only if one of the dates are available
  return (
    ((hasStartDate || hasEndDate) && !(hasStartDate && hasEndDate)) ||
    // Or if the start date is less than or equal to the end date
    (hasStartDate && hasEndDate && startDateTime <= endDateTime) ||
    // Or if there is no start or end date
    !(hasStartDate && hasEndDate)
  );
};

export const getDuration = ({ duration, media, type }) =>
  type === MEDIA_KEYS.TYPE.VIDEOS.KEY
    ? media.runtime || INVALID_DURATION
    : duration;

export const hasChangedSinceLastPublish = (clip, latestJob) => {
  const lastPublishDate = new Date(latestJob.created);
  const cliplastUpdate = new Date(clip.media.last_updated);

  return (
    !clip.isNewItem && // content was part of last publish
    clip.type !== MEDIA_KEYS.TYPE.VIDEOS.KEY && // ignore videos (see ENGHCAPPS-2305)
    lastPublishDate < cliplastUpdate // content has been updated since
  );
};

export const createTimelineListWithOptGroups = (timelineList, group) => {
  const timelineListWithOptGroups = new Map();
  timelineList
    .sort((a, b) => a.id - b.id)
    .forEach((tl) => {
      if (timelineListWithOptGroups.has(tl.type)) {
        timelineListWithOptGroups.get(tl.type).options.push(tl);
      } else {
        const optGroupTitle = `${tl.type
          .charAt(0)
          .toUpperCase()}${tl.type.slice(1)} ${
          group.platform !== 'Ocean' ? 'Channels' : 'Videos'
        }`;
        timelineListWithOptGroups.set(tl.type, {
          optGroupTitle,
          options: [tl],
        });
      }
    });

  return Array.from(timelineListWithOptGroups.values());
};

export const getTimelineDuration = (timeline) => {
  // ASSUMPTION: timeline.playlist is empty if there is no selected timeline
  let durationIsApproximate = false;

  const timelineClips = timeline.playlist || [];
  const durationInSeconds = timelineClips.reduce((duration, clip) => {
    if (clip.hasDuration) {
      duration += clip.useDefaultDuration
        ? timeline.default_duration
        : clip.duration;
    } else {
      // Handle videos who aren't reporting duration yet.
      durationIsApproximate = true;
    }
    return duration;
  }, 0);

  const date = new Date(0, 0, 0, 0, 0, durationInSeconds, 0).toISOString();

  return {
    value: parseDate(date, DATE_FORMATS.mmss),
    isApproximate: durationIsApproximate,
  };
};

export const createTimelineClip = ({
  currentClipId,
  data,
  order = null,
  timelineSelected,
}) => {
  const auxClip = {
    clipId: currentClipId + 1,
    media: data,
    order,
    type: data.type,
    isNewItem: false,
    title: data.title,
    showDropdown: false,
  };

  const newClip = {
    ...auxClip,
    isInvalid: isInvalid(auxClip),
    isValidDateRange: getIsValidDateRange(auxClip),
    hasThumbnail: hasThumbnail(auxClip),
    useDefaultDuration: getUseDefaultDuration({
      type: auxClip.type,
      duration: auxClip.duration,
    }),
    active: {
      ...getClipStartEndDate(auxClip),
    },
    duration: getDuration({
      duration: auxClip.duration,
      media: auxClip.media,
      type: auxClip.type,
    }),
    minDuration: MIN_DURATION,
    maxDuration: MAX_DURATION,
  };
  const hasDuration =
    newClip.duration !== INVALID_DURATION || newClip.useDefaultDuration;
  const changedSinceLastPublish = hasChangedSinceLastPublish(
    newClip,
    timelineSelected.latest_job,
  );

  return {
    ...newClip,
    hasDuration,
    hasChangedSinceLastPublish: changedSinceLastPublish,
  };
};

export const getRegionColors = (region) => {
  const background_color = region.background_color
    ? region.background_color
    : '#ffffff';
  const bg = parseHexToColor(background_color);
  const foreground_color = region.foreground_color
    ? region.foreground_color
    : '#ffffff';

  return {
    background_color,
    bg,
    foreground_color,
  };
};

export const getImageData = (url) =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const image = new Image();
    image.setAttribute('crossorigin', 'anonymous');
    image.src = url;
    image.onload = () => {
      canvas.height = image.height;
      canvas.width = image.width;

      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg');
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    image.onerror = () => {
      reject(new Error('Something went wrong'));
    };
  });

export const getTimelineListWithLabels = ({
  timelineList,
  happeningsChannelSettings,
}) => {
  const filteredgroups = timelineList.filter((item) => item.type === 'group');
  const filteredHappenings = timelineList
    .toSorted((a, b) => a.id - b.id)
    .filter((item) => item.type !== 'group')
    .map((tl, index) => ({
      ...tl,
      label: happeningsChannelSettings.channel_settings[index].label,
    }));
  const timelineListWithLabels = filteredHappenings.concat(filteredgroups);
  return timelineListWithLabels;
};

export const getTagsUrls = (slide, parentFolder, mediaNode) => {
  const apiUri = defaultConfig.apiUri;

  if (slide && slide.id) {
    if (parentFolder && parentFolder.parent_id !== null) {
      return [buildApiUri(apiUri, ENDPOINTS.TAGS, parentFolder.id)];
    } else {
      return [];
    }
  } else {
    if (mediaNode && mediaNode.parent_id !== null) {
      return [buildApiUri(apiUri, ENDPOINTS.TAGS, mediaNode.id)];
    } else {
      return [];
    }
  }
};
